import { useState } from 'react';
import { toast } from '@/components/ui/sonner';
import { useAccountUsers } from '@/hooks/use-account-users';
import { useEscalationPolicy, useHelpdeskAgents, useUpdateEscalationPolicy } from '@/hooks/use-escalation-policy';
import { usePermissions } from '@/hooks/use-permissions';
import { Icon } from '@/modules/protocols/components/ui';
import { ProtocolEmailModal } from '@/modules/protocols/components/protocol-email-modal';
import { ProtocolFieldsModal, type FieldSpec } from '@/modules/protocols/components/protocol-fields-modal';
import { actionMeta, type EscalationActionId } from '@/modules/protocols/constants';
import '@/modules/protocols/protocol.css';
import {
	assignEmailRefusal,
	assignFields,
	assignFromValues,
	assignValues,
	ASSIGN_TARGET_LABELS,
	ESCALATION_TYPE_LABELS,
	POLICY_ESCALATION_TYPES,
	SLACK_FIELDS,
	sentinelFields,
	sentinelFromValues,
	sentinelUsers,
	sentinelValues,
	UNASSIGN_POLICY_FIELD,
	unassignTargetOf,
	type EscalationType,
} from './escalation-policy';

const ACTION_OF: Record<EscalationType, EscalationActionId> = {
	'send-email': 'email',
	assign: 'assign',
	unassign: 'escalation-unassign',
	'send-private-note': 'note',
	'send-slack-message': 'slack',
};

const NOTE_FIELDS: FieldSpec[] = [{ key: 'noteContent', label: 'Note', placeholder: 'Escalated by Kim AI — please pick this up.', multiline: true }];

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** One line saying what the policy sends, or that it is not set. */
function summary(type: EscalationType, fields: Record<string, string>): string {
	switch (type) {
		case 'send-email':
			return [fields['to'] && `To ${fields['to']}`, fields['subject']].filter(Boolean).join(' · ');
		case 'assign': {
			const agent = fields['agentProvider'] ? `${fields['agentName']} · ${capitalize(fields['agentProvider'])}` : fields['agentEmail'];
			const sentinel = fields['sentinelUserName'] || fields['sentinelUserEmail'];
			return [agent && `Agent: ${agent}`, sentinel && `Sentinel: ${sentinel}`].filter(Boolean).join(' · ');
		}
		case 'unassign': {
			const target = unassignTargetOf(fields);
			return target ? `Target: ${ASSIGN_TARGET_LABELS[target]}` : '';
		}
		case 'send-private-note':
			return fields['noteContent'] ?? '';
		case 'send-slack-message':
			return [fields['slackChannel'] && `#${fields['slackChannel']}`, fields['slackMessage']].filter(Boolean).join(' · ');
	}
}

/**
 * Settings → Escalations → Escalation policy: the default each escalation falls back to. Each row opens
 * the same composer a protocol step uses; whatever a step leaves blank comes from here.
 */
export function EscalationPolicySection() {
	const { data: policy, isLoading, isError } = useEscalationPolicy();
	const { data: agents = [], isLoading: agentsLoading } = useHelpdeskAgents();
	const { data: accountUsers, isLoading: usersLoading } = useAccountUsers();
	const sentinels = sentinelUsers(accountUsers ?? []);
	const update = useUpdateEscalationPolicy();
	const { can } = usePermissions();
	const canEdit = can('edit', 'brand');
	const [editing, setEditing] = useState<EscalationType | null>(null);

	const save = async (type: EscalationType, fields: Record<string, string>) => {
		try {
			await update.mutateAsync({ ...policy, [type]: fields });
			toast.success(`${ESCALATION_TYPE_LABELS[type]} policy saved`);
			setEditing(null);
		} catch (error) {
			console.error('[EscalationPolicySection] save failed', { type, error });
			toast.error('Could not save the escalation policy');
		}
	};

	const current = (type: EscalationType) => policy?.[type] ?? {};
	return (
		// protocol-ui: the composers are the protocol editor's, and read its design tokens.
		<div id='escalation-policy' className='protocol-ui w-[600px] flex flex-col gap-3'>
			<div className='flex flex-col gap-1'>
				<p className='text-[13px] font-medium text-gray-800 leading-[18px]'>Escalation policy</p>
				<p className='text-[11px] text-gray-800 leading-4'>
					The default for each escalation. A protocol step or instruction uses it for anything it leaves blank.
				</p>
			</div>

			{isError ? (
				<div className='rounded-[16px] border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-600'>
					Failed to load the escalation policy. Please try again later.
				</div>
			) : (
				<div className='bg-white border border-gray-500 rounded-[16px] overflow-hidden divide-y divide-gray-300'>
					{POLICY_ESCALATION_TYPES.map((type) => {
						const meta = actionMeta(ACTION_OF[type]);
						const line = isLoading ? '' : summary(type, current(type));
						return (
							<div key={type} className='flex items-center gap-3 px-4 py-3'>
								<span className='flex size-8 shrink-0 items-center justify-center rounded-[8px] bg-red-100'>
									{meta ? <Icon src={meta.chipIcon} size={14} /> : null}
								</span>
								<div className='flex min-w-0 flex-1 flex-col'>
									<p className='text-[13px] font-medium text-ink leading-5'>{ESCALATION_TYPE_LABELS[type]}</p>
									<p className='truncate text-[12px] text-gray-800 leading-4'>{line || 'Not set'}</p>
								</div>
								<button
									type='button'
									// Assign waits for the agents and Sentinels, so its saved defaults show in the lists.
									disabled={!canEdit || isLoading || (type === 'assign' && (agentsLoading || usersLoading))}
									aria-label={`${line ? 'Edit' : 'Set up'} ${ESCALATION_TYPE_LABELS[type]} policy`}
									onClick={() => setEditing(type)}
									className='h-[30px] shrink-0 px-3 rounded-[20px] border border-gray-300 bg-white text-[12px] font-medium text-ink transition-colors cursor-pointer hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50'
								>
									{line ? 'Edit' : 'Set up'}
								</button>
							</div>
						);
					})}
				</div>
			)}

			{editing === 'send-email' ? (
				<ProtocolEmailModal
					policyMode
					existing={policy?.['send-email'] ? { to: '', cc: '', bcc: '', subject: '', body: '', ...policy['send-email'] } : null}
					onSave={(data) => void save('send-email', { ...data })}
					onClose={() => setEditing(null)}
				/>
			) : null}
			{editing === 'send-slack-message' ? (
				<ProtocolFieldsModal
					title='Send Slack Message · brand policy'
					fields={SLACK_FIELDS}
					initial={{ slackChannel: '', slackMessage: '', ...current('send-slack-message') }}
					onSave={(values) => void save('send-slack-message', { ...values, slackChannel: (values['slackChannel'] ?? '').replace(/^#/, '') })}
					onClose={() => setEditing(null)}
				/>
			) : null}
			{editing === 'send-private-note' ? (
				<ProtocolFieldsModal
					title='Send Private Note · brand policy'
					fields={NOTE_FIELDS}
					initial={{ noteContent: '', ...current('send-private-note') }}
					onSave={(values) => void save('send-private-note', values)}
					onClose={() => setEditing(null)}
				/>
			) : null}
			{editing === 'unassign' ? (
				// The target an Unassign step acts on when it leaves its own blank.
				<ProtocolFieldsModal
					title='Unassign · brand policy'
					fields={[UNASSIGN_POLICY_FIELD]}
					initial={{ assignTarget: unassignTargetOf(current('unassign')) }}
					onSave={(values) => {
						const target = unassignTargetOf(values);
						void save('unassign', target ? { assignTarget: target } : {});
					}}
					onClose={() => setEditing(null)}
				/>
			) : null}
			{editing === 'assign' ? (
				// One default per target: a step takes the one for the target it assigns.
				<ProtocolFieldsModal
					title='Assign · brand policy'
					fields={[
						...assignFields(
							agents,
							agents.length ? 'No default agent' : 'No agents listed by this helpdesk, use an email',
							current('assign'),
						),
						...sentinelFields(sentinels, sentinels.length ? 'No default Sentinel' : 'No users with the Sentinel role', current('assign')),
					]}
					initial={{ ...assignValues(current('assign')), ...sentinelValues(current('assign')) }}
					validate={assignEmailRefusal}
					onSave={(values) => {
						const agent = assignFromValues(values, agents, current('assign'));
						const sentinel = sentinelFromValues(values, sentinels, current('assign'));
						void save('assign', {
							...(agent['agentId'] || agent['agentEmail'] ? agent : {}),
							...(sentinel['sentinelUserId'] ? sentinel : {}),
						});
					}}
					onClose={() => setEditing(null)}
				/>
			) : null}
		</div>
	);
}
